import { GLOBAL_CONFIG } from "@/global-config";
import { t } from "@/locales/i18n";
import type { ControllerResult } from "@/types/express/controller_result";
import { auth } from "@/utils/firebase";
import axios, { type AxiosRequestConfig, type AxiosError, type AxiosResponse } from "axios";
import { toast } from "sonner";

const axiosInstance = axios.create({
	baseURL: GLOBAL_CONFIG.apiBaseUrl,
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8" },
});

axiosInstance.interceptors.request.use(
	async (config) => {
		const user = auth.currentUser;
		if (user) {
			const token = await user.getIdToken();
			config.headers.Authorization = `Bearer ${token}`;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
	(res: AxiosResponse<ControllerResult<any>>) => {
		if (!res.data) throw new Error(t("sys.api.apiRequestFailed"));

		const { code, data, message } = res.data;
		const SUCCESS_CODES = [200, 201];
		if (SUCCESS_CODES.includes(code)) {
			return data;
		}

		throw new Error(message || t("sys.api.apiRequestFailed"));
	},
	(error: AxiosError<ControllerResult<any>>) => {
		const { config, response, message } = error || {};
		const status = response?.status;
		const silent =
			(config as (AxiosRequestConfig & { silentErrorToast?: boolean; silentErrorStatuses?: number[] }) | undefined)
				?.silentErrorToast ||
			(status != null &&
				(
					config as (AxiosRequestConfig & { silentErrorStatuses?: number[] }) | undefined
				)?.silentErrorStatuses?.includes(status));
		if (!silent) {
			const errMsg = response?.data?.message || message || t("sys.api.errorMessage");
			toast.error(errMsg, { position: "top-center" });
		}
		if (status === 401) {
			auth.signOut();
		}
		return Promise.reject(error);
	},
);

class APIClient {
	get<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "GET" });
	}
	post<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "POST" });
	}
	put<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PUT" });
	}
	delete<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "DELETE" });
	}
	patch<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PATCH" });
	}
	request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return axiosInstance.request<any, T>(config);
	}
}

export default new APIClient();
